import { useState } from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { PageIntro } from '../components/layout/PageIntro';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Send, CheckCircle, Trash2, FileSearch } from 'lucide-react';
import axios from 'axios';
import { useSeo } from '../hooks/useSeo';
import { PAGE_SEO } from '../seo/site';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const DatenschutzPage = () => {
  useSeo(PAGE_SEO.datenschutz);
  const [formData, setFormData] = useState({
    type: 'inquiry',
    name: '',
    email: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      await axios.post(`${API}/privacy/request`, formData);
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Anfrage konnte nicht gesendet werden');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Header />
      <main data-testid="datenschutz-page">
        <PageIntro eyebrow="Rechtliches" title="Datenschutzerklärung" />
        <div className="wrap max-w-4xl sec--tight">
          {/* Privacy Request Form */}
          <div className="card-ink card-ink--shadow p-6 md:p-8 mb-12" data-testid="privacy-request-card">
            <p className="label-caps text-purple mb-2">DSGVO</p>
            <h2 className="font-display font-bold text-2xl mb-3">Ihre Datenschutzrechte</h2>
            <p className="text-[1rem] leading-relaxed text-ink/90 mb-6">
              Sie haben das Recht auf Auskunft über Ihre gespeicherten Daten sowie auf Löschung. 
              Nutzen Sie das folgende Formular, um eine Anfrage zu stellen.
            </p>
            
            {submitted ? (
              <div className="card-ink card-ink--lilac p-6 text-center" data-testid="privacy-request-success">
                <CheckCircle className="w-12 h-12 text-purple mx-auto mb-4" />
                <h3 className="font-display font-bold text-xl mb-2">Anfrage erfolgreich übermittelt</h3>
                <p>
                  Wir werden uns innerhalb von 30 Tagen bei Ihnen melden.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5" data-testid="privacy-request-form">
                <div>
                  <Label className="field-label">Art der Anfrage</Label>
                  <div className="flex flex-col sm:flex-row gap-4">
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, type: 'inquiry' }))}
                      className={`flex-1 p-4 border-2 border-ink transition-colors ${
                        formData.type === 'inquiry' ? 'bg-purple text-off shadow-ink' : 'bg-off hover:bg-lilac'
                      }`}
                      data-testid="privacy-type-inquiry"
                    >
                      <FileSearch className="w-6 h-6 mx-auto mb-2" />
                      <p className="font-display font-bold text-lg">Datenauskunft</p>
                      <p className="text-xs opacity-80 mt-1">Art. 15 DSGVO</p>
                    </button>
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, type: 'deletion' }))}
                      className={`flex-1 p-4 border-2 border-ink transition-colors ${
                        formData.type === 'deletion' ? 'bg-red-700 text-white shadow-ink' : 'bg-off hover:bg-lilac'
                      }`}
                      data-testid="privacy-type-deletion"
                    >
                      <Trash2 className="w-6 h-6 mx-auto mb-2" />
                      <p className="font-display font-bold text-lg">Datenlöschung</p>
                      <p className="text-xs opacity-80 mt-1">Art. 17 DSGVO</p>
                    </button>
                  </div>
                </div>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="privacy-name" className="field-label">
                      Ihr Name
                    </Label>
                    <Input
                      id="privacy-name"
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="Max Mustermann"
                    />
                  </div>
                  <div>
                    <Label htmlFor="privacy-email" className="field-label">
                      E-Mail-Adresse *
                    </Label>
                    <Input
                      id="privacy-email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                      placeholder="ihre@email.de"
                      required
                    />
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="privacy-message" className="field-label">
                    Nachricht (optional)
                  </Label>
                  <Textarea
                    id="privacy-message"
                    value={formData.message}
                    onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                    placeholder="Weitere Informationen zu Ihrer Anfrage..."
                    rows={4}
                  />
                </div>
                
                {error && <p className="text-red-700 font-medium" data-testid="privacy-request-error">{error}</p>}
                
                <Button type="submit" disabled={loading} data-testid="privacy-request-submit">
                  {loading ? 'Wird gesendet...' : (
                    <>
                      <Send className="w-4 h-4" />
                      Anfrage absenden
                    </>
                  )}
                </Button>
              </form>
            )}
          </div>
          
          <div className="space-y-8">
            <section>
              <h2 className="font-display font-bold text-2xl mb-3">1. Datenschutz auf einen Blick</h2>
              <h3 className="font-display font-bold text-lg mb-2">Allgemeine Hinweise</h3>
              <p className="text-[1rem] leading-relaxed text-ink/90">
                Die folgenden Hinweise geben einen einfachen Überblick darüber, was mit Ihren personenbezogenen Daten 
                passiert, wenn Sie diese Website besuchen. Personenbezogene Daten sind alle Daten, mit denen Sie 
                persönlich identifiziert werden können. Ausführliche Informationen zum Thema Datenschutz entnehmen 
                Sie unserer unter diesem Text aufgeführten Datenschutzerklärung.
              </p>
            </section>

            <section>
              <h2 className="font-display font-bold text-2xl mb-3">2. Datenerfassung auf dieser Website</h2>
              <h3 className="font-display font-bold text-lg mb-2">Wer ist verantwortlich für die Datenerfassung auf dieser Website?</h3>
              <p className="text-[1rem] leading-relaxed text-ink/90">
                Die Datenverarbeitung auf dieser Website erfolgt durch den Websitebetreiber. Dessen Kontaktdaten 
                können Sie dem Impressum dieser Website entnehmen.
              </p>

              <h3 className="font-display font-bold text-lg mb-2 mt-5">Wie erfassen wir Ihre Daten?</h3>
              <p className="text-[1rem] leading-relaxed text-ink/90">
                Ihre Daten werden zum einen dadurch erhoben, dass Sie uns diese mitteilen. Hierbei kann es sich z. B. 
                um Daten handeln, die Sie in ein Kontaktformular eingeben.
              </p>
              <p className="text-[1rem] leading-relaxed text-ink/90 mt-2">
                Andere Daten werden automatisch oder nach Ihrer Einwilligung beim Besuch der Website durch unsere 
                IT-Systeme erfasst. Das sind vor allem technische Daten (z. B. Internetbrowser, Betriebssystem oder 
                Uhrzeit des Seitenaufrufs).
              </p>

              <h3 className="font-display font-bold text-lg mb-2 mt-5">Wofür nutzen wir Ihre Daten?</h3>
              <p className="text-[1rem] leading-relaxed text-ink/90">
                Ein Teil der Daten wird erhoben, um eine fehlerfreie Bereitstellung der Website zu gewährleisten. 
                Andere Daten können zur Analyse Ihres Nutzerverhaltens verwendet werden. Wenn Sie über unser 
                Kontaktformular eine Anfrage stellen, nutzen wir Ihre Daten zur Bearbeitung dieser Anfrage.
              </p>

              <h3 className="font-display font-bold text-lg mb-2 mt-5">Welche Rechte haben Sie bezüglich Ihrer Daten?</h3>
              <p className="text-[1rem] leading-relaxed text-ink/90">
                Sie haben jederzeit das Recht, unentgeltlich Auskunft über Herkunft, Empfänger und Zweck Ihrer 
                gespeicherten personenbezogenen Daten zu erhalten. Sie haben außerdem ein Recht, die Berichtigung 
                oder Löschung dieser Daten zu verlangen. Wenn Sie eine Einwilligung zur Datenverarbeitung erteilt 
                haben, können Sie diese Einwilligung jederzeit für die Zukunft widerrufen.
              </p>
            </section>

            <section>
              <h2 className="font-display font-bold text-2xl mb-3">3. Hosting</h2>
              <p className="text-[1rem] leading-relaxed text-ink/90">
                Wir hosten die Inhalte unserer Website bei folgendem Anbieter: Der externe Hoster wird unsere Daten 
                nur insoweit verarbeiten, wie dies zur Erfüllung seiner Leistungspflichten erforderlich ist und 
                unsere Weisungen in Bezug auf diese Daten befolgen.
              </p>
            </section>

            <section>
              <h2 className="font-display font-bold text-2xl mb-3">4. Allgemeine Hinweise und Pflichtinformationen</h2>
              <h3 className="font-display font-bold text-lg mb-2">Datenschutz</h3>
              <p className="text-[1rem] leading-relaxed text-ink/90">
                Die Betreiber dieser Seiten nehmen den Schutz Ihrer persönlichen Daten sehr ernst. Wir behandeln 
                Ihre personenbezogenen Daten vertraulich und entsprechend den gesetzlichen Datenschutzvorschriften 
                sowie dieser Datenschutzerklärung.
              </p>

              <h3 className="font-display font-bold text-lg mb-2 mt-5">Hinweis zur verantwortlichen Stelle</h3>
              <p className="text-[1rem] leading-relaxed text-ink/90">
                Die verantwortliche Stelle für die Datenverarbeitung auf dieser Website ist:<br /><br />
                GND Gastro Next Digital UG (haftungsbeschränkt)<br />
                Spritzenplatz 16<br />
                22765 Hamburg<br /><br />
                Telefon: 0178 726 1209<br />
                E-Mail: maximilian@gastro-beratung.de
              </p>
            </section>

            <section>
              <h2 className="font-display font-bold text-2xl mb-3">5. Datenerfassung auf dieser Website</h2>
              <h3 className="font-display font-bold text-lg mb-2">Kontaktformular</h3>
              <p className="text-[1rem] leading-relaxed text-ink/90">
                Wenn Sie uns per Kontaktformular Anfragen zukommen lassen, werden Ihre Angaben aus dem 
                Anfrageformular inklusive der von Ihnen dort angegebenen Kontaktdaten zwecks Bearbeitung der 
                Anfrage und für den Fall von Anschlussfragen bei uns gespeichert. Diese Daten geben wir nicht 
                ohne Ihre Einwilligung weiter.
              </p>
              <p className="text-[1rem] leading-relaxed text-ink/90 mt-2">
                Die Verarbeitung dieser Daten erfolgt auf Grundlage von Art. 6 Abs. 1 lit. b DSGVO, sofern Ihre 
                Anfrage mit der Erfüllung eines Vertrags zusammenhängt oder zur Durchführung vorvertraglicher 
                Maßnahmen erforderlich ist. In allen übrigen Fällen beruht die Verarbeitung auf unserem berechtigten 
                Interesse an der effektiven Bearbeitung der an uns gerichteten Anfragen (Art. 6 Abs. 1 lit. f DSGVO) 
                oder auf Ihrer Einwilligung (Art. 6 Abs. 1 lit. a DSGVO) sofern diese abgefragt wurde.
              </p>
            </section>

            <section>
              <h2 className="font-display font-bold text-2xl mb-3">6. Plugins und Tools</h2>
              <h3 className="font-display font-bold text-lg mb-2">YouTube</h3>
              <p className="text-[1rem] leading-relaxed text-ink/90">
                Diese Website bindet Videos der Website YouTube ein. Betreiber der Website ist die Google Ireland 
                Limited („Google"), Gordon House, Barrow Street, Dublin 4, Irland.
              </p>
              <p className="text-[1rem] leading-relaxed text-ink/90 mt-2">
                Wenn Sie eine unserer Webseiten besuchen, auf denen YouTube eingebunden ist, wird eine Verbindung 
                zu den Servern von YouTube hergestellt. Dabei wird dem YouTube-Server mitgeteilt, welche unserer 
                Seiten Sie besucht haben.
              </p>
              <p className="text-[1rem] leading-relaxed text-ink/90 mt-2">
                Die Nutzung von YouTube erfolgt im Interesse einer ansprechenden Darstellung unserer Online-Angebote. 
                Dies stellt ein berechtigtes Interesse im Sinne von Art. 6 Abs. 1 lit. f DSGVO dar.
              </p>
            </section>

            <section>
              <h2 className="font-display font-bold text-2xl mb-3">7. Newsletter / E-Mail Marketing</h2>
              <p className="text-[1rem] leading-relaxed text-ink/90">
                Wenn Sie sich für unseren Newsletter anmelden, verwenden wir die hierfür erforderlichen oder 
                gesondert von Ihnen mitgeteilten Daten, um Ihnen regelmäßig unseren E-Mail-Newsletter zuzusenden. 
                Die Abmeldung vom Newsletter ist jederzeit möglich und kann entweder durch eine Nachricht an uns 
                oder über einen dafür vorgesehenen Link im Newsletter erfolgen.
              </p>
            </section>

            <section>
              <h2 className="font-display font-bold text-2xl mb-3">8. Ihre Rechte</h2>
              <p className="text-[1rem] leading-relaxed text-ink/90">
                Sie haben gegenüber uns folgende Rechte hinsichtlich der Sie betreffenden personenbezogenen Daten:
              </p>
              <ul className="list-none mt-3 space-y-2 text-[1rem] text-ink/90 [&>li]:flex [&>li]:gap-2 [&>li]:before:content-['✳'] [&>li]:before:text-purple">
                <li>Recht auf Auskunft</li>
                <li>Recht auf Berichtigung oder Löschung</li>
                <li>Recht auf Einschränkung der Verarbeitung</li>
                <li>Recht auf Widerspruch gegen die Verarbeitung</li>
                <li>Recht auf Datenübertragbarkeit</li>
              </ul>
              <p className="text-[1rem] leading-relaxed text-ink/90 mt-4">
                Sie haben zudem das Recht, sich bei einer Datenschutz-Aufsichtsbehörde über die Verarbeitung Ihrer 
                personenbezogenen Daten durch uns zu beschweren.
              </p>
            </section>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
};

export default DatenschutzPage;
